import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Webhook, KeyRound, ChevronRight, ChevronLeft, Check,
  Loader2, Rocket, ExternalLink, Info, ArrowRight, DollarSign,
  Tag, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// 4-step wizard purpose-built for API/SaaS creators. Replaces the generic
// product-creation form for technical creators who'd otherwise have to
// figure out images, file uploads, license-key delivery, and pricing on
// their own. Walks them from zero → live listing in ~3 minutes.

const PRESETS = [
  {
    id: 'starter',
    label: 'Starter Pack',
    price: 29,
    headline: 'Best for first-time buyers',
    features: [
      'Lower price = lower buyer hesitation',
      'Funnels into your subscription tier',
      'Recommended pricing: $19-$39',
    ],
  },
  {
    id: 'pro',
    label: 'Pro Tier',
    price: 79,
    headline: 'Highest LTV',
    features: [
      'Recurring revenue, 5-10x LTV vs one-time',
      'Anchor product on your storefront',
      'Recommended pricing: $49-$149/mo',
    ],
  },
  {
    id: 'lifetime',
    label: 'Founder / Lifetime',
    price: 199,
    headline: 'For your first 50 buyers',
    features: [
      'Generates testimonials + word-of-mouth',
      'High AOV, builds initial traction',
      'Limit supply via License Key Vault',
    ],
  },
];

const STEPS = [
  { id: 'basics', label: 'Basics' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'launch', label: 'Launch' },
];

export default function ApiCreatorQuickstart() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [createdProductId, setCreatedProductId] = useState(null);
  const [createdProduct, setCreatedProduct] = useState(null);
  const [stripeBlocked, setStripeBlocked] = useState(false);
  const [stripeChecked, setStripeChecked] = useState(false);

  // Wizard state
  const [form, setForm] = useState({
    preset: 'starter',
    name: '',
    description: '',
    category_id: '',
    price: 29,
    features: '',
    deliveryMethod: 'webhook', // webhook | license_key_vault
    webhookUrl: '',
    licenseKeysText: '',
    instructions: '',
  });

  // Check Stripe-Connect / payout-method precondition before letting them
  // invest 3 minutes in the wizard. Creators with payout_method=stripe but
  // no Stripe Connect account hit a 403 at launch — surface that upfront.
  useEffect(() => {
    const check = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/stripe/connect/status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const eligibility = await axios.get('/creator/payout-eligibility', {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: {} }));
        const altRail = ['manual_wise', 'stripe_global'].includes(
          eligibility.data?.current_method
        );
        const stripeReady = res.data?.charges_enabled || res.data?.payouts_enabled;
        if (!stripeReady && !altRail) setStripeBlocked(true);
      } catch {
        // If status endpoint fails we'll let them try and surface the 403
        // at launch as a fallback — better than locking them out entirely.
      } finally {
        setStripeChecked(true);
      }
    };
    check();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/categories');
        const list = Array.isArray(res.data) ? res.data : res.data?.categories || [];
        setCategories(list);
        if (list.length && !form.category_id) {
          setForm((f) => ({ ...f, category_id: list[0].category_id }));
        }
      } catch {
        toast.error('Could not load categories');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const p = PRESETS.find((x) => x.id === preset);
    setForm((f) => ({
      ...f,
      preset,
      price: p?.price || f.price,
    }));
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.name.trim() || !form.description.trim()) {
        toast.error('Name and description are required');
        return false;
      }
      if (!form.category_id) {
        toast.error('Pick a category');
        return false;
      }
    }
    if (step === 1) {
      if (form.deliveryMethod === 'webhook') {
        const url = (form.webhookUrl || '').trim();
        if (!url) {
          toast.error('Webhook URL is required');
          return false;
        }
        if (url.startsWith('http://')) {
          toast.error('Webhook URL must use HTTPS — http:// is not allowed for security');
          return false;
        }
        if (!url.startsWith('https://')) {
          toast.error('Webhook URL must start with https://');
          return false;
        }
      } else if (!form.licenseKeysText.trim()) {
        toast.error('Paste at least one license key');
        return false;
      }
    }
    if (step === 2) {
      const n = parseFloat(form.price);
      if (isNaN(n) || n < 1) {
        toast.error('Price must be at least $1');
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const launch = async () => {
    if (!validateStep()) return;
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Create the product
      const featuresArr = form.features
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const productRes = await axios.post(
        '/products',
        {
          category_id: form.category_id,
          name: form.name.trim(),
          description: form.description.trim(),
          price: parseFloat(form.price),
          images: [],
          features: featuresArr.length ? featuresArr : ['API access included'],
        },
        { headers }
      );
      const product = productRes.data;
      setCreatedProductId(product.product_id);
      setCreatedProduct(product);

      // 2. Configure delivery method
      const deliveryBody =
        form.deliveryMethod === 'webhook'
          ? {
              delivery_method: 'webhook',
              webhook_url: form.webhookUrl.trim(),
              instructions: form.instructions.trim() || null,
            }
          : {
              delivery_method: 'license_key_vault',
              instructions: form.instructions.trim() || null,
            };
      const deliveryRes = await axios.put(
        `/products/${product.product_id}/delivery`,
        deliveryBody,
        { headers }
      );

      // 3. If vault: upload the keys
      if (form.deliveryMethod === 'license_key_vault') {
        const blob = new Blob([form.licenseKeysText], { type: 'text/csv' });
        const fd = new FormData();
        fd.append('file', blob, 'keys.csv');
        await axios.post(
          `/products/${product.product_id}/license-keys/upload`,
          fd,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
      }

      toast.success('Product launched 🎉');
      // Update displayed product with delivery config
      setCreatedProduct((p) => ({ ...p, ...deliveryRes.data }));
      setStep(STEPS.length - 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Launch failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Stripe-Connect precondition gate — shown before the wizard
          becomes interactive if the creator hasn't completed Stripe
          onboarding (or set an alternate payout rail). */}
      {stripeChecked && stripeBlocked && (
        <div
          data-testid="quickstart-stripe-gate"
          className="border-b border-amber-500/30 bg-amber-500/5"
        >
          <div className="mx-auto flex max-w-3xl flex-wrap items-center gap-3 px-4 py-3 md:px-8">
            <div className="rounded-md bg-amber-500/15 p-1.5 text-amber-400">
              <Info className="h-4 w-4" />
            </div>
            <div className="flex-1 text-sm text-amber-100">
              <strong>Connect Stripe to launch.</strong>{' '}
              <span className="text-amber-200/80">
                You can fill the wizard now, but the final launch step needs
                Stripe payouts wired up. Connect Stripe in your dashboard, then
                come back — your inputs will still be here.
              </span>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="bg-amber-400 text-black hover:bg-amber-400/90"
            >
              Open dashboard
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-white/10 bg-gradient-to-b from-[#00F0FF]/5 to-transparent">
        <div className="mx-auto max-w-3xl px-4 py-8 md:px-8 md:py-10">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-[#00F0FF]/15 p-2 text-[#00F0FF]">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF]">
              Quick-start · API & SaaS creators
            </span>
          </div>
          <h1
            data-testid="api-quickstart-heading"
            className="mt-3 text-3xl font-bold sm:text-4xl"
          >
            Launch an API product in 3 minutes
          </h1>
          <p className="mt-2 text-white/60">
            We'll handle billing, delivery, and the customer relationship — you
            just provision the keys.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 md:px-8 md:py-10">
        {/* Step indicator */}
        <div className="mb-8 flex items-center justify-between">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div
                data-testid={`step-indicator-${s.id}`}
                className={`flex items-center gap-2 ${
                  i === step ? 'text-[#00F0FF]' : i < step ? 'text-[#00FF94]' : 'text-white/30'
                }`}
              >
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                    i === step
                      ? 'bg-[#00F0FF] text-black'
                      : i < step
                      ? 'bg-[#00FF94] text-black'
                      : 'border border-white/20 bg-neutral-900'
                  }`}
                >
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className="hidden text-sm font-medium sm:inline">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`mx-2 h-0.5 flex-1 ${
                    i < step ? 'bg-[#00FF94]' : 'bg-white/10'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div className="rounded-2xl border border-white/10 bg-neutral-950 p-6 md:p-8">
          {step === 0 && (
            <BasicsStep form={form} setForm={setForm} categories={categories} />
          )}
          {step === 1 && <DeliveryStep form={form} setForm={setForm} />}
          {step === 2 && (
            <PricingStep form={form} setForm={setForm} applyPreset={applyPreset} />
          )}
          {step === 3 && (
            <LaunchStep
              form={form}
              createdProductId={createdProductId}
              createdProduct={createdProduct}
              submitting={submitting}
              onLaunch={launch}
              onView={() => navigate('/dashboard')}
            />
          )}
        </div>

        {/* Nav buttons */}
        {step < STEPS.length - 1 && (
          <div className="mt-6 flex justify-between">
            <Button
              data-testid="wizard-back"
              variant="outline"
              onClick={back}
              disabled={step === 0}
              className="border-white/20 bg-transparent text-white hover:bg-white/5 disabled:opacity-30"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            {step < STEPS.length - 2 ? (
              <Button
                data-testid="wizard-next"
                onClick={next}
                className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
              >
                Continue
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                data-testid="wizard-launch"
                onClick={launch}
                disabled={submitting}
                className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Rocket className="mr-2 h-4 w-4" />
                )}
                Launch product
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const BasicsStep = ({ form, setForm, categories }) => (
  <div className="space-y-5">
    <Header
      kicker="Step 01"
      title="What are you selling?"
      sub="Keep the name punchy and the description specific. 'Increase Open Rates 32%' beats 'Best Email AI'."
    />
    <Field
      label="Product name"
      testId="quickstart-name"
      value={form.name}
      onChange={(v) => setForm({ ...form, name: v })}
      placeholder="e.g. Cold Email AI · Starter Pack"
    />
    <FieldArea
      label="Description"
      testId="quickstart-description"
      value={form.description}
      onChange={(v) => setForm({ ...form, description: v })}
      placeholder="What does it do, who is it for, what makes it different? 2-3 sentences."
      rows={4}
    />
    <FieldArea
      label="Key features (one per line)"
      testId="quickstart-features"
      value={form.features}
      onChange={(v) => setForm({ ...form, features: v })}
      placeholder={`5,000 API calls/mo\nGPT-4o + Claude routing\nNo rate limits\nDiscord support`}
      rows={4}
      hint="Buyers scan these like a checklist. Lead with quantitative + concrete."
    />
    <div>
      <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
        Category
      </Label>
      <select
        data-testid="quickstart-category"
        value={form.category_id}
        onChange={(e) => setForm({ ...form, category_id: e.target.value })}
        className="w-full rounded-md border border-white/10 bg-neutral-900 px-3 py-2 text-sm text-white"
      >
        {categories.map((c) => (
          <option key={c.category_id} value={c.category_id}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  </div>
);

const DeliveryStep = ({ form, setForm }) => (
  <div className="space-y-5">
    <Header
      kicker="Step 02"
      title="How do buyers get access?"
      sub="Pick one. You can change it later."
    />

    <div className="grid gap-3">
      <DeliveryOption
        testId="quickstart-delivery-webhook"
        icon={<Webhook className="h-4 w-4" />}
        title="Webhook (recommended for SaaS)"
        subtitle="We POST to your API on each sale; you provision a fresh key per buyer"
        selected={form.deliveryMethod === 'webhook'}
        onSelect={() => setForm({ ...form, deliveryMethod: 'webhook' })}
      />
      <DeliveryOption
        testId="quickstart-delivery-vault"
        icon={<KeyRound className="h-4 w-4" />}
        title="License Key Vault"
        subtitle="Pre-upload a list of pre-generated keys; we hand one to each buyer in order"
        selected={form.deliveryMethod === 'license_key_vault'}
        onSelect={() => setForm({ ...form, deliveryMethod: 'license_key_vault' })}
      />
    </div>

    {form.deliveryMethod === 'webhook' ? (
      <div className="space-y-3 rounded-lg border border-white/10 bg-neutral-900 p-4">
        <Field
          label="Your provisioning endpoint URL"
          testId="quickstart-webhook-url"
          value={form.webhookUrl}
          onChange={(v) => setForm({ ...form, webhookUrl: v })}
          placeholder="https://api.yourapp.com/theailedger-purchase"
        />
        <p className="text-xs text-white/50">
          Need help wiring it up?{' '}
          <a
            href="/docs/webhooks"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00F0FF] hover:underline"
            data-testid="quickstart-docs-link"
          >
            Read the webhook docs <ExternalLink className="inline h-3 w-3" />
          </a>
          {' '}— we have copy-paste examples in Python, Node, and Go.
        </p>
      </div>
    ) : (
      <div className="space-y-3 rounded-lg border border-white/10 bg-neutral-900 p-4">
        <FieldArea
          label="Paste your license keys (one per line)"
          testId="quickstart-keys-text"
          value={form.licenseKeysText}
          onChange={(v) => setForm({ ...form, licenseKeysText: v })}
          placeholder={`KEY-AAA-001\nKEY-AAA-002\nKEY-AAA-003`}
          rows={6}
        />
        <div className="flex items-start gap-2 rounded-md bg-amber-500/5 p-2 text-xs text-amber-200/90">
          <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-400" />
          <span>
            We'll pop one key per sale. Restock anytime from the dashboard. Each
            key only gets used once.
          </span>
        </div>
      </div>
    )}

    <FieldArea
      label="Optional: instructions to include in the buyer's email"
      testId="quickstart-instructions"
      value={form.instructions}
      onChange={(v) => setForm({ ...form, instructions: v })}
      placeholder="Use your key in the X-API-Key header. Quickstart: https://yourapp.com/docs/start"
      rows={2}
    />
  </div>
);

const PricingStep = ({ form, setForm, applyPreset }) => (
  <div className="space-y-5">
    <Header
      kicker="Step 03"
      title="Pick a pricing strategy"
      sub="These are the three plays we see API creators run. You can list multiple — start with one."
    />

    <div className="grid gap-3">
      {PRESETS.map((p) => (
        <button
          key={p.id}
          data-testid={`preset-${p.id}`}
          type="button"
          onClick={() => applyPreset(p.id)}
          className={`rounded-xl border p-4 text-left transition-colors ${
            form.preset === p.id
              ? 'border-[#00F0FF] bg-[#00F0FF]/5'
              : 'border-white/10 bg-neutral-900 hover:border-white/30'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Tag className={`h-4 w-4 ${form.preset === p.id ? 'text-[#00F0FF]' : 'text-white/50'}`} />
                <span className={`text-sm font-semibold ${form.preset === p.id ? 'text-[#00F0FF]' : 'text-white'}`}>
                  {p.label}
                </span>
                <span className="rounded-full bg-[#00FF94]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#00FF94]">
                  {p.headline}
                </span>
              </div>
              <ul className="mt-2 space-y-0.5">
                {p.features.map((f) => (
                  <li key={f} className="text-xs text-white/60">
                    • {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-white/40">starting at</div>
              <div className={`text-2xl font-bold ${form.preset === p.id ? 'text-[#00F0FF]' : 'text-white'}`}>
                ${p.price}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>

    <div className="rounded-lg border border-white/10 bg-neutral-900 p-4">
      <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
        Custom price (USD)
      </Label>
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-white/50" />
        <Input
          data-testid="quickstart-custom-price"
          type="number"
          step="0.01"
          min="1"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
          className="bg-neutral-950 border-white/10 text-white"
        />
      </div>
    </div>
  </div>
);

const LaunchStep = ({ form, createdProductId, createdProduct, submitting, onLaunch, onView }) => {
  if (!createdProductId) {
    // Pre-launch summary
    return (
      <div className="space-y-5">
        <Header
          kicker="Step 04"
          title="Ready to launch?"
          sub="Quick review. Hit launch when it looks right."
        />
        <SummaryRow label="Product name" value={form.name || '—'} />
        <SummaryRow label="Description" value={form.description ? form.description.slice(0, 120) + (form.description.length > 120 ? '…' : '') : '—'} />
        <SummaryRow label="Price" value={`$${form.price}`} accent="text-[#00FF94]" />
        <SummaryRow
          label="Delivery"
          value={
            form.deliveryMethod === 'webhook'
              ? `Webhook → ${form.webhookUrl || '(empty)'}`
              : `License Key Vault (${form.licenseKeysText.split('\n').filter(Boolean).length} keys)`
          }
        />
        <Button
          data-testid="wizard-launch-from-summary"
          onClick={onLaunch}
          disabled={submitting}
          className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
        >
          {submitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Rocket className="mr-2 h-4 w-4" />
          )}
          Launch product
        </Button>
      </div>
    );
  }

  // Post-launch success state
  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#00FF94]/15">
        <Check className="h-8 w-8 text-[#00FF94]" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white">Your product is live 🚀</h2>
        <p className="mt-1 text-white/60">
          Find it on your dashboard, or share the link with your audience to start
          driving traffic.
        </p>
      </div>
      <div
        data-testid="wizard-success-product-id"
        className="rounded-lg border border-[#00FF94]/30 bg-[#00FF94]/5 p-3 font-mono text-sm text-[#00FF94]"
      >
        {createdProductId}
      </div>
      {form.deliveryMethod === 'webhook' && createdProduct?.webhook_secret && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-left">
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-amber-300">
            <Zap className="h-3.5 w-3.5" />
            Save your webhook secret now
          </div>
          <code className="block break-all rounded bg-black/40 p-2 font-mono text-xs text-white">
            {createdProduct.webhook_secret}
          </code>
          <p className="mt-2 text-xs text-amber-200/80">
            Use this on your server to verify the X-Theailedger-Signature header.
            You can re-view it any time from the product's delivery settings.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          data-testid="wizard-view-dashboard"
          onClick={onView}
          variant="outline"
          className="flex-1 border-white/20 bg-transparent text-white hover:bg-white/5"
        >
          Open dashboard
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
        <Button
          onClick={() => window.location.reload()}
          className="flex-1 bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
        >
          Launch another
        </Button>
      </div>
    </div>
  );
};

const Header = ({ kicker, title, sub }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF]">
      {kicker}
    </div>
    <h2 className="mt-1 text-2xl font-bold text-white">{title}</h2>
    {sub && <p className="mt-1 text-sm text-white/60">{sub}</p>}
  </div>
);

const Field = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
      {label}
    </Label>
    <Input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30"
    />
  </div>
);

const FieldArea = ({ label, value, onChange, placeholder, testId, rows = 3, hint }) => (
  <div>
    <Label className="mb-1 block text-xs uppercase tracking-wider text-white/50">
      {label}
    </Label>
    <Textarea
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="bg-neutral-900 border-white/10 text-white placeholder:text-white/30"
    />
    {hint && <p className="mt-1 text-xs text-white/40">{hint}</p>}
  </div>
);

const DeliveryOption = ({ testId, icon, title, subtitle, selected, onSelect }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onSelect}
    className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${
      selected
        ? 'border-[#00F0FF] bg-[#00F0FF]/5'
        : 'border-white/10 bg-neutral-900 hover:border-white/30'
    }`}
  >
    <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-md ${
      selected ? 'bg-[#00F0FF]/15 text-[#00F0FF]' : 'bg-white/5 text-white/60'
    }`}>
      {icon}
    </div>
    <div className="flex-1">
      <div className={`text-sm font-medium ${selected ? 'text-[#00F0FF]' : 'text-white'}`}>
        {title}
      </div>
      <div className="text-xs text-white/50">{subtitle}</div>
    </div>
    {selected && <Check className="h-4 w-4 text-[#00F0FF]" />}
  </button>
);

const SummaryRow = ({ label, value, accent }) => (
  <div className="flex items-start justify-between gap-4 border-b border-white/5 pb-3">
    <span className="text-xs uppercase tracking-wider text-white/50">{label}</span>
    <span className={`text-right text-sm font-medium ${accent || 'text-white'}`}>
      {value}
    </span>
  </div>
);
